import { Navigate, type RouteObject } from 'react-router-dom'
import {
  ArrowsLeftRight,
  Bandaids,
  ListNumbers,
  MagnifyingGlass,
  Flag,
  SealCheck,
  Stack,
  Truck,
  Warehouse,
} from '@phosphor-icons/react'
import { ROLES } from '@/auth/roles'
import type { PhoneTileDef } from '@/features/phone/tiles'
import { LoadPage } from './pages/LoadPage'
import { ReceivePage } from './pages/ReceivePage'
import { PutAwayPage } from './pages/PutAwayPage'
import { MovePage } from './pages/MovePage'
import { CountPage } from './pages/CountPage'
import { DeclarationPage } from './pages/DeclarationPage'
import { StockPage } from './pages/StockPage'
import { RedTiePage } from './pages/RedTiePage'
import { ReplacePage } from './pages/ReplacePage'
import { StoreByScope } from './v1/ByScope'
import { V1Declaration } from './v1/Form'
import { V1GodownHome } from './v1/Home'
import { V1Load } from './v1/Load'
import { V1Move, V1PutAway } from './v1/PlaceFlow'
import { V1Count } from './v1/Count'
import { V1Stock } from './v1/Stock'
import { V1RedTie } from './v1/RedTie'
import { V1Replace } from './v1/Replace'
import { V1Receive } from './v1/Receive'

/**
 * Store (F5) phone flows, mounted under /m. Loading happens at the collection point (village
 * device) or at a site for a trip between our sites; receive / declaration / put away / move /
 * count / stock / red ties / torn bag happen at a site (store device). Under Seva Mandir V1 the
 * store screens are the V1 boards (./v1).
 */
export const storeRoutes: RouteObject[] = [
  {
    path: 'store',
    element: <StoreByScope v1={<V1GodownHome />} full={<Navigate to="/m" replace />} />,
  },
  { path: 'store/load', element: <StoreByScope v1={<V1Load />} full={<LoadPage />} /> },
  // Same screen on a store phone (a trip between sites); a separate path keeps tile keys unique.
  { path: 'store/transfer', element: <StoreByScope v1={<V1Load />} full={<LoadPage />} /> },
  { path: 'store/receive', element: <StoreByScope v1={<V1Receive />} full={<ReceivePage />} /> },
  {
    path: 'store/declaration',
    element: <StoreByScope v1={<V1Declaration />} full={<DeclarationPage />} />,
  },
  {
    path: 'store/put-away',
    element: <StoreByScope v1={<V1PutAway />} full={<PutAwayPage />} />,
  },
  { path: 'store/move', element: <StoreByScope v1={<V1Move />} full={<MovePage />} /> },
  { path: 'store/count', element: <StoreByScope v1={<V1Count />} full={<CountPage />} /> },
  { path: 'store/stock', element: <StoreByScope v1={<V1Stock />} full={<StockPage />} /> },
  { path: 'store/red-tie', element: <StoreByScope v1={<V1RedTie />} full={<RedTiePage />} /> },
  { path: 'store/replace', element: <StoreByScope v1={<V1Replace />} full={<ReplacePage />} /> },
]

const KEEPER = [ROLES.STORE_KEEPER, ROLES.SUPERVISOR]

export const storeTiles: PhoneTileDef[] = [
  {
    to: '/m/store/load',
    title: 'store.load',
    hint: 'store.loadHint',
    icon: Truck,
    team: 'VILLAGE',
    roles: [ROLES.VILLAGE_OPERATOR, ...KEEPER],
  },
  {
    to: '/m/store/receive',
    title: 'store.receive',
    hint: 'store.receiveHint',
    icon: Warehouse,
    team: 'SITE',
    roles: KEEPER,
    primary: true,
  },
  {
    to: '/m/store/put-away',
    title: 'store.putAway',
    hint: 'store.putAwayHint',
    icon: Stack,
    team: 'SITE',
    roles: KEEPER,
  },
  {
    to: '/m/store/declaration',
    title: 'store.declaration',
    hint: 'store.declarationHint',
    icon: SealCheck,
    team: 'SITE',
    roles: KEEPER,
  },
  {
    to: '/m/store/move',
    title: 'store.move',
    hint: 'store.moveHint',
    icon: ArrowsLeftRight,
    team: 'SITE',
    roles: KEEPER,
  },
  {
    to: '/m/store/count',
    title: 'store.count',
    hint: 'store.countHint',
    icon: ListNumbers,
    team: 'SITE',
    roles: KEEPER,
  },
  {
    to: '/m/store/stock',
    title: 'store.stock',
    hint: 'store.stockHint',
    icon: MagnifyingGlass,
    team: 'SITE',
    roles: KEEPER,
  },
  {
    to: '/m/store/red-tie',
    title: 'store.redTie',
    hint: 'store.redTieHint',
    icon: Flag,
    team: 'SITE',
    roles: KEEPER,
  },
  {
    to: '/m/store/replace',
    title: 'store.replace.title',
    hint: 'store.replace.hint',
    icon: Bandaids,
    team: 'SITE',
    roles: KEEPER,
  },
  {
    to: '/m/store/transfer',
    title: 'store.transfer',
    hint: 'store.transferHint',
    icon: Truck,
    team: 'SITE',
    roles: KEEPER,
  },
]
